import type { ScheduleEventData } from "@mantine/schedule";

import dayjs from "dayjs";

import type { ScheduleBlockStatus, ScheduleResponse } from "./types";

import { BUSINESS_TIMEZONE } from "./businessDay";

// AgendaView shows these times exactly as written, so they're converted from UTC to the business timezone first.
const SCHEDULE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

function toBusinessTime(isoUtc: string): string {
    return dayjs.utc(isoUtc).tz(BUSINESS_TIMEZONE).format(SCHEDULE_TIME_FORMAT);
}

/** Extra data attached to each event, which ScheduleView uses to pick the row's icon and show its duration. */
export interface ScheduleEventPayload {
    status: ScheduleBlockStatus;
    durationMinutes: number;
}

/** One AgendaView event per schedule block, with times in the business timezone. */
export function toScheduleEvents(schedule: ScheduleResponse | null): ScheduleEventData[] {
    if (!schedule) return [];

    // The index keeps ids unique: AgendaView crashes on duplicate ids, and two blocks can share a status and start
    // time (e.g. LATE and a cancelled huddle both at the start of the shift).
    return schedule.blocks.map((block, index) => ({
        id: `${index}-${block.status}-${block.startTime}`,
        // The backend's display text (e.g. "Break"), else the raw Aspect code (e.g. "PSS NEW QUEUE"), else "Scheduled".
        title: block.displayLabel?.trim() || block.label?.trim() || "Scheduled",
        start: toBusinessTime(block.startTime),
        end: toBusinessTime(block.endTime),
        // AgendaView requires a color, but it's never shown: ScheduleView draws its own rows.
        color: "dark",
        payload: {
            status: block.status,
            durationMinutes: block.durationMinutes,
        } satisfies ScheduleEventPayload,
    }));
}

/** Business-timezone "h:mm a" label for the shift start (e.g. "10:00 am"), or null with no shift. */
export function getShiftStartLabel(schedule: ScheduleResponse | null): string | null {
    if (!schedule?.workStart) return null;
    return dayjs.utc(schedule.workStart).tz(BUSINESS_TIMEZONE).format("h:mm a");
}
